import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  submitPatchJobs,
  pollPatchJobStatus,
  pollMultiplePatchJobs,
  PatchJobStatusResponse,
  JobResponse,
  AggregatedPatchJobStatus,
} from '../../../services/api';
import { clearPendingChanges, selectPendingChanges } from '../patchesSlice';
import { mapPatchOperations } from '../utils/mapPatchOperations';
import { AppDispatch } from '../../../app/store';
import { useUser } from '../../../services/auth/auth.service';

// Matches backend PatchJobRequestDto contract
export interface PatchJobRequestDto {
  patchIds: string[];
  setOperations?: Array<{
    setVariantPrice?: {
      variantId: string;
      authoringPrice: {
        amount: number;
      };
    };
    setBundlePrice?: {
      bundleId: string;
      authoringPrice: {
        amount: number;
      };
    };
    setModifierWeightPrice?: {
      variantId: string;
      slotId: string;
      modifierId: string;
      weightId: string;
      authoringPrice: {
        amount: number;
      };
    };
    setModifierPriceRanges?: {
      variantId: string;
      slotId: string;
      weightPriceRanges: Array<{
        rangeStart: number;
        rangeEnd: number;
        authoringPrice?: {
          amount: number;
        };
        applyModifierWeightUpcharge: boolean;
      }>;
    };
  }>;
  removeOperations?: Array<{
    removeBundle?: {
      bundleId: string;
    };
    removeProduct?: {
      productId: string;
    };
    removeVariant?: {
      variantId: string;
    };
  }>;
}

interface SubmitStatus {
  isSubmitting: boolean;
  success: boolean;
  error: string | null;
  jobStatus?: {
    jobId?: number | string;
    jobIds?: string[];
    status: string;
    message?: string;
    progress?: number;
    total?: number;
    totalSubJobs?: number;
    totalSubJobsCompleted?: number;
    totalSubJobsFailed?: number;
    totalSubJobsInProgress?: number;
    startedAt?: string;
    completedAt?: string;
  };
}

/**
 * Custom hook for handling patch submission logic
 * Manages the submission of pending changes to patches
 */
export const useSubmitPatches = () => {
  const dispatch = useDispatch<AppDispatch>();
  const pendingChanges = useSelector(selectPendingChanges);
  const { data: userData } = useUser(true);
  const currentUser = userData?.data;
  
  const [status, setStatus] = useState<SubmitStatus>({
    isSubmitting: false,
    success: false,
    error: null,
  });

  // No RTK Query mutation, use Axios function
  const isLoading = status.isSubmitting;

  /**
   * Reset the status to initial state
   */
  const resetStatus = useCallback(() => {
    setStatus({
      isSubmitting: false,
      success: false,
      error: null,
    });
  }, []);

  /**
   * Polls the patch job status using the jobId (Axios version)
   */
  const pollJobStatus = useCallback(
    async (jobId: number | string): Promise<PatchJobStatusResponse> => {
      // Use the Axios-based getPatchJobStatus function
      return pollPatchJobStatus(
        jobId.toString(),
        async (id: string) => {
          return await (await import('../../../services/api')).getPatchJobStatus(id);
        },
        {
          pollingInterval: 2000,
          timeout: 60000,
          onProgress: (jobStatus) => {
            setStatus((prevStatus) => ({
              ...prevStatus,
              jobStatus: {
                jobId: jobStatus.jobId,
                status: jobStatus.status,
                message: jobStatus.message,
                totalSubJobs: jobStatus.totalSubJobs,
                totalSubJobsCompleted: jobStatus.totalSubJobsCompleted,
                totalSubJobsFailed: jobStatus.totalSubJobsFailed,
                totalSubJobsInProgress: jobStatus.totalSubJobsInProgress,
                startedAt: jobStatus.startedAt,
                completedAt: jobStatus.completedAt,
              },
            }));
          },
          onComplete: (jobStatus) => {
            console.log(`Job ${jobId} completed with status: ${jobStatus.status}`);
          },
          onError: (error) => {
            console.error(`Error polling job ${jobId}:`, error);
            setStatus((prevStatus) => ({
              ...prevStatus,
              error: `Error polling job: ${error.message}`,
            }));
          },
        }
      );
    },
    []
  );

  /**
   * Polls multiple patch jobs in parallel and aggregates their status
   */
  const pollMultipleJobsStatus = useCallback(
    async (jobIds: string[]): Promise<AggregatedPatchJobStatus> => {
      return pollMultiplePatchJobs(
        jobIds,
        async (id: string) => {
          return await (await import('../../../services/api')).getPatchJobStatus(id);
        },
        {
          pollingInterval: 2000,
          timeout: 60000,
          onProgress: (aggregatedStatus) => {
            setStatus((prevStatus) => ({
              ...prevStatus,
              jobStatus: {
                jobIds: aggregatedStatus.jobIds,
                status: aggregatedStatus.overallStatus,
                message: aggregatedStatus.message,
                progress: aggregatedStatus.totalSubJobsCompleted,
                total: aggregatedStatus.totalSubJobs,
                totalSubJobs: aggregatedStatus.totalSubJobs,
                totalSubJobsCompleted: aggregatedStatus.totalSubJobsCompleted,
                totalSubJobsFailed: aggregatedStatus.totalSubJobsFailed,
                totalSubJobsInProgress: aggregatedStatus.totalSubJobsInProgress,
                startedAt: aggregatedStatus.startedAt,
                completedAt: aggregatedStatus.completedAt,
              },
            }));
          },
          onComplete: (aggregatedStatus) => {
            console.log(`All jobs completed with overall status: ${aggregatedStatus.overallStatus}`);
          },
          onError: (error) => {
            console.error(`Error polling multiple jobs:`, error);
            setStatus((prevStatus) => ({
              ...prevStatus,
              error: `Error polling jobs: ${error.message}`,
            }));
          },
        }
      );
    },
    []
  );

  /**
   * Submit the pending changes to the patches API
   */
  const submitPatches = useCallback(async () => {
    // Check if there are pending changes to submit
    if (
      !pendingChanges.id ||
      (!pendingChanges.data.setOperations.length && 
       !pendingChanges.data.removeOperations.length)
    ) {
      const errorMsg = 'No pending changes to submit';
      console.error(errorMsg);
      setStatus({
        isSubmitting: false,
        success: false,
        error: errorMsg,
      });
      return;
    }

    setStatus({
      isSubmitting: true,
      success: false,
      error: null,
    });

    try {
      // Check if user is available
      if (!currentUser?.loginId) {
        throw new Error('User not authenticated or user ID not available');
      }

      // Map the patch IDs to unique set for processing
      const patchIds = Array.from(new Set(pendingChanges.data.patchIds));

      // Always unwrap to minimal before mapping for API
      const setOperations = mapPatchOperations(
        (pendingChanges.data.setOperations || []).map(op => op.minimal ?? op)
      );
      const removeOperations = mapPatchOperations(
        (pendingChanges.data.removeOperations || []).map(op => op.minimal ?? op),
        true
      );

      // Create the request payload for the new bulk pricing API (flattened structure)
      const patchJobRequest: PatchJobRequestDto = {
        patchIds,
      };

      // Only include setOperations if there are any
      if (setOperations && setOperations.length > 0) {
        patchJobRequest.setOperations = setOperations;
      }

      // Only include removeOperations if there are any
      if (removeOperations && removeOperations.length > 0) {
        patchJobRequest.removeOperations = removeOperations;
      }

      // Submit the patch job using the GraphQL mutation
      const submitResult = await submitPatchJobs(patchJobRequest, currentUser.loginId);
      
      // Handle both single job and multiple jobs response
      let finalJobStatus: PatchJobStatusResponse | AggregatedPatchJobStatus;
      
      // Check if response contains multiple jobs or a single job
      if (Array.isArray(submitResult)) {
        // Multiple jobs returned
        const jobIds = submitResult.map((job: JobResponse) => job.jobId.toString());
        finalJobStatus = await pollMultipleJobsStatus(jobIds);
      } else if (submitResult.jobs && Array.isArray(submitResult.jobs)) {
        // Jobs wrapped in a jobs array
        const jobIds = submitResult.jobs.map((job: JobResponse) => job.jobId.toString());
        finalJobStatus = await pollMultipleJobsStatus(jobIds);
      } else {
        // Check if jobId contains comma-separated values (multiple job IDs in a single string)
        const { jobId } = submitResult as JobResponse;
        
        if (!jobId) {
          throw new Error('No job ID returned from patch submission');
        }
        
        const jobIdStr = jobId.toString();
        
        if (jobIdStr.includes(',')) {
          // Multiple job IDs in comma-separated format
          const jobIds = jobIdStr.split(',').map(id => id.trim()).filter(Boolean);
          finalJobStatus = await pollMultipleJobsStatus(jobIds);
        } else {
          // Single job returned
          finalJobStatus = await pollJobStatus(jobId);
        }
      }

      // Clear the pending changes only if the job(s) completed successfully
      const isSuccessful = 'overallStatus' in finalJobStatus 
        ? finalJobStatus.overallStatus === 'COMPLETED'
        : finalJobStatus.status === 'COMPLETED';

      if (isSuccessful) {
        dispatch(clearPendingChanges());

        setStatus({
          isSubmitting: false,
          success: true,
          error: null,
          jobStatus: 'overallStatus' in finalJobStatus
            ? {
                jobIds: finalJobStatus.jobIds,
                status: finalJobStatus.overallStatus,
                message: finalJobStatus.message,
                progress: finalJobStatus.totalSubJobsCompleted,
                total: finalJobStatus.totalSubJobs,
                totalSubJobs: finalJobStatus.totalSubJobs,
                totalSubJobsCompleted: finalJobStatus.totalSubJobsCompleted,
                totalSubJobsFailed: finalJobStatus.totalSubJobsFailed,
                totalSubJobsInProgress: finalJobStatus.totalSubJobsInProgress,
                startedAt: finalJobStatus.startedAt,
                completedAt: finalJobStatus.completedAt,
              }
            : {
                jobId: finalJobStatus.jobId,
                status: finalJobStatus.status,
                message: finalJobStatus.message,
                totalSubJobs: finalJobStatus.totalSubJobs,
                totalSubJobsCompleted: finalJobStatus.totalSubJobsCompleted,
                totalSubJobsFailed: finalJobStatus.totalSubJobsFailed,
                totalSubJobsInProgress: finalJobStatus.totalSubJobsInProgress,
                startedAt: finalJobStatus.startedAt,
                completedAt: finalJobStatus.completedAt,
              },
        });
      } else {
        const status = 'overallStatus' in finalJobStatus ? finalJobStatus.overallStatus : finalJobStatus.status;
        const message = finalJobStatus.message || '';
        const errorMsg = `Patch job(s) failed with status: ${status}. ${message}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Error submitting patches:', error);
      setStatus({
        isSubmitting: false,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit changes',
      });
      throw error; // Re-throw so the calling code can handle it
    }
  }, [pendingChanges, submitPatchJobs, dispatch, pollJobStatus, pollMultipleJobsStatus, currentUser]);

  return {
    submitPatches,
    resetStatus,
    isSubmitting: status.isSubmitting || isLoading,
    success: status.success,
    error: status.error,
    jobStatus: status.jobStatus,
    hasPendingChanges: !!(
      pendingChanges.id &&
      (pendingChanges.data.setOperations.length > 0 || 
       pendingChanges.data.removeOperations.length > 0)
    ),
  };
};
